<template>
  <div>
    <Header
      :title="$t('workflowDesigner.edit') + ' ' + resolveLocalizedString(process.name)"
      :subtitle="subtitle"
      :show-back-button="true"
      :items="headerItems"
      @back="$router.push({ name: 'projectWorkflows' })"
      @save="saveData"
      @showVersions="onShowVersions"
    >
      <template #contentRight>
        <v-chip
          class="ml-2"
          :class="'flow-type-' + process.flow_type"
          small
          :label="true"
        >
          {{
            $t(
              "processConfiguration.flowTypes." +
                (process.flow_type ?? "STANDARD")
            )
          }}
        </v-chip>
      </template>
    </Header>
    <div class="card card-custom">
      <div class="card-body">
        <FormHelper
          ref="form"
          v-model="process"
          :form="form"
          :config="config"
        />
      </div>
    </div>
    <BackupModal
      v-if="process && process.workflow"
      v-model="showVersionsModal"
      :object="process.workflow"
      classname="Workflow"
    />
  </div>
</template>

<script>
import Workflows from "@/components/Workflows/Designer/workflows";
import ProcessManager from "@/components/Workflows/processManager";
import Header from "@/components/Tools/Header/Header.vue";
import FormHelper from "@/components/Tools/FormHelper/FormHelper.vue";
import {
  addEventToLoadingQueue,
  removeEventFromLoadingQueue
} from "@/composables/useLoadingQueue";
import FlowTableEntries from "@/components/Workflows/Designer/Components/flowTableEntries";
import Queues from "@/components/Settings/Queues/queues";
import { mapGetters } from "vuex";
import { formatDate } from "@/components/Tools/helperFunctions";
import BackupModal from "@/components/Backup/BackupModal.vue";
import { useCurrentProjectFilter } from "@/composables/useCurrentProjectFilter";
import { useWorkNote } from "@/composables/useWorkNote";
import { FlowType } from "@/components/Workflows/FlowType";
import FlowCategories from "@/components/Workflows/flowCategories";
import { resolveLocalizedString } from "@/composables/useLocalizedDescription";

export default {
  components: {
    BackupModal,
    FormHelper,
    Header
  },
  data() {
    return {
      config: {
        snippetPrefix: "process",
        enableVariables: false
      },
      form: [
        {
          label: this.$t("workflowDesigner.name"),
          name: "name",
          type: "localizedInput",
          disableReturnType: true,
          default: "",
          validations: {
            required: true,
            minLength: 3
          }
        },
        {
          label: this.$t("workflowDesigner.description"),
          name: "description",
          type: "localizedTextEditor",
          validations: {
            required: false
          }
        },
        {
          label: this.$t("workflowDesigner.active"),
          name: "active",
          type: "select",
          default: "active",
          options: [
            {
              label: this.$t("workflowDesigner.active"),
              value: "active"
            },
            {
              label: this.$t("workflowDesigner.inactive"),
              value: "inactive"
            },
            {
              label: this.$t("workflowDesigner.advanced"),
              value: "advanced"
            }
          ]
        },
        {
          type: "condition",
          name: "active_condition",
          enableVariables: true,
          label: this.$t("workflowDesigner.activeCondition"),
          dependsOn: [
            {
              name: "active",
              values: ["advanced"]
            }
          ]
        },
        {
          label: this.$t("workflowDesigner.projectChoose"),
          name: "projects",
          type: "multiselect",
          clearable: true,
          options: [],
          validations: {
            required: true
          }
        },
        {
          label: this.$t("workflowDesigner.queueChoose"),
          name: "queue_id",
          type: "select",
          options: [],
          validations: {
            required: true
          }
        },
        {
          label: this.$t("workflowDesigner.setParameters"),
          name: "parameters",
          type: "group",
          fields: [
            {
              name: "text",
              label: this.$t("processConfiguration.name"),
              type: "text",
              validations: { required: true }
            },
            {
              name: "type",
              label: this.$t("processConfiguration.type"),
              type: "select",
              options: ["boolean", "integer", "float", "string"]
            },
            {
              name: "value",
              label: this.$t("processConfiguration.defaultValue"),
              type: "text",
              dependsOn: [{ name: "type", values: ["string"] }]
            },
            {
              name: "value",
              label: this.$t("processConfiguration.defaultValue"),
              type: "checkbox",
              dependsOn: [{ name: "type", values: ["boolean"] }]
            },
            {
              name: "value",
              label: this.$t("processConfiguration.defaultValue"),
              type: "number",
              dependsOn: [{ name: "type", values: ["integer", "float"] }]
            },
            {
              name: "description",
              label: this.$t("processConfiguration.description"),
              type: "textarea",
              rows: 3
            }
          ],
          validations: {
            required: false
          }
        },
        {
          label: this.$t("workflowDesigner.managedByProcessEngine"),
          name: "managed_by_process_engine",
          type: "checkbox",
          default: false
        },
        {
          label: this.$t("workflowDesigner.isTemplate"),
          name: "is_template",
          type: "checkbox",
          default: false
        },
        {
          label: this.$t("workflowDesigner.category"),
          name: "flow_categories",
          type: "multiselect",
          options: []
        }
      ],
      process: {
        id: "",
        name: "",
        description: "",
        projects: [],
        queue_id: "",
        workflow: {},
        active_condition: {
          type: "group",
          operator: "and",
          children: [
            {
              type: "condition",
              field: "",
              operator: "==",
              value: "",
              valid: true
            }
          ],
          valid: true
        },
        managed_by_process_engine: false,
        flow_categories: []
      },
      workflows: [],
      showVersionsModal: false
    };
  },
  computed: {
    ...mapGetters([
      "projects",
      "selectedProject",
      "selectedPresetVersion",
      "isPresetProject",
      "isDevPresetVersion"
    ]),
    ...mapGetters("instance", ["instance"]),
    isBusy: function () {
      return this.$store.getters["loadingQueue/showLoadingBar"]();
    },
    headerItems: function () {
      let headerItems = [
        {
          type: "button",
          icon: this.$store.getters["config/icons"].version,
          tooltip: this.$t("general.showVersions"),
          class: "btn-outline-primary",
          emit: "showVersions",
          disabledWhenLoading: true
        }
      ];
      if (this.isPresetProject || !this.isDevPresetVersion) {
        return headerItems;
      }
      headerItems.push({
        type: "button",
        title: this.$t("general.save"),
        emit: "save",
        disabledWhenLoading: true
      });
      return headerItems;
    },
    subtitle: function () {
      if (!this.process.workflow) {
        return "";
      }

      let subtitleParts = [];
      if (this.process.workflow.updated_by_user?.full_name) {
        subtitleParts.push(this.process.workflow.updated_by_user?.full_name);
      }
      if (this.process.workflow.updated_at) {
        subtitleParts.push(formatDate(this.process.workflow.updated_at));
      }
      if (subtitleParts.length === 0) {
        return "";
      }

      return subtitleParts.join(", ");
    }
  },
  watch: {
    projects: function () {
      this.setProjects();
    },
    selectedPresetVersion: function () {
      this.initialize();
    }
  },
  mounted() {
    this.initialize();
  },
  methods: {
    resolveLocalizedString,
    initialize() {
      this.loadWorkflow();
      this.setProjects();
      this.loadWorkflows();
      this.loadQueues();
      this.loadFlowCategories();
    },
    loadWorkflow() {
      addEventToLoadingQueue({ key: "loadWorkflow" });
      let id = this.$route.params.id;

      ProcessManager.get(id)
        .then(response => {
          let process = response.data;

          this.process = process;
          this.process.description =
            process.workflow.description !== "undefined"
              ? process.workflow.description
              : "";
          this.process.projects = process.workflow.projects.map(
            project => project.id
          );
          this.process.queue_id = process.queue?.id;
          this.process.workflow.id = process.workflow.id;
          this.process.managed_by_process_engine =
            process.flow_type === FlowType.PROCESS_ENGINE;

          if (process.flow_categories?.length > 0) {
            this.process.flow_categories = process.flow_categories?.map(
              category => category.identifier
            );
          }

          this.process.parameters = this.process.parameters?.map(parameter => {
            if (parameter.type === "boolean") {
              const value = parameter.value;
              parameter.value =
                value === true ||
                value === "true" ||
                value === 1 ||
                value === "1";
            }
            return parameter;
          });

          if (
            process.flow_type === FlowType.CSV_IMPORTER ||
            process.flow_type === FlowType.CUSTOMIZING
          ) {
            this.form = this.form.filter(
              field => field.name !== "managed_by_process_engine"
            );
          }

          removeEventFromLoadingQueue({ key: "loadWorkflow" });
        })
        .catch(error => {
          this.$error(error);
        });
    },
    async saveData() {
      let invalidParams = this.process.parameters?.find(
        p => !p.text || p.text.trim() === ""
      );
      if (invalidParams) {
        this.$error(
          this.$t("formHelper.errors.required", { label: "parameters.*.name" })
        );
        return;
      }
      let process = this.process;
      process.updated_by_user_id = this.$store.getters.userData.id;

      const { addWorkNote } = useWorkNote();
      const { data, success } = await addWorkNote(process);

      if (!success) {
        return;
      }

      // remove tokens to avoid overwriting them with a Model->sync
      delete data["flow_tokens"];

      if (data.managed_by_process_engine === true) {
        data.flow_type = FlowType.PROCESS_ENGINE;
      } else if (data.flow_type === FlowType.PROCESS_ENGINE) {
        // we don't want to reset other flow types like CSV_IMPORTER
        data.flow_type = FlowType.STANDARD;
      }
      delete data.managed_by_process_engine;

      addEventToLoadingQueue({ key: "saveData" });

      ProcessManager.update(process.id, data)
        .then(() => {
          this.saveWorkflow();
        })
        .catch(error => {
          this.$error(error);
        })
        .finally(() => removeEventFromLoadingQueue({ key: "saveData" }));
    },
    saveWorkflow() {
      addEventToLoadingQueue({ key: "saveWorkflow" });
      Workflows.update(this.process.workflow.id, this.process)
        .then(() => {
          removeEventFromLoadingQueue({
            key: "saveWorkflow",
            type: "success",
            prefix: "workflowDesigner",
            name: "workflowSaved"
          });

          this.$router.push({ name: "projectWorkflows" });
        })
        .catch(error => {
          this.$error(error);
        });
    },
    setProjects() {
      // Fills multiselect projects
      const formProjects = Object.values(this.form).find(
        element => element.name === "projects"
      );
      formProjects.options = this.getProjectOptions();
    },
    setQueues() {
      const formQueues = Object.values(this.form).find(
        element => element.name === "queue_id"
      );
      formQueues.options = this.getQueues();
    },
    loadWorkflows() {
      addEventToLoadingQueue({ key: "loadWorkflows" });
      // Load more than 15 workflows
      let params = {
        perPage: 100
      };
      const { filter } = useCurrentProjectFilter();

      FlowTableEntries.getAll(params, filter.value)
        .then(response => {
          this.workflows = response.data;
          removeEventFromLoadingQueue({ key: "loadWorkflows" });
        })
        .catch(error => {
          this.$error(error);
        });
    },
    loadQueues() {
      const params = {
        page: 1,
        perPage: 100
      };
      const { filter } = useCurrentProjectFilter();

      Queues.getAll(params, filter.value)
        .then(response => {
          this.queues = response.data;
          this.setQueues();
        })
        .catch(error => {
          this.$error(error);
        });
    },
    loadFlowCategories() {
      FlowCategories.getAll().then(response => {
        const formCategories = Object.values(this.form).find(
          element => element.name === "flow_categories"
        );
        formCategories.options = this.mapFlowCategories(response.data ?? []);
      });
    },
    mapFlowCategories(flowCategories, prefix = "") {
      const categories = [];

      flowCategories.forEach(flowCategory => {
        const label = prefix
          ? prefix + " / " + flowCategory.name
          : flowCategory.name;
        categories.push({
          label: label,
          value: flowCategory.identifier
        });
        if (!flowCategory.children) return;
        categories.push(
          ...this.mapFlowCategories(flowCategory.children, label)
        );
      });
      return categories;
    },
    getProjectOptions() {
      return this.projects.map(project => ({
        label: project.name,
        value: project.id
      }));
    },
    getQueues() {
      return this.queues.map(queue => ({
        label: queue.raw_name,
        value: queue.id
      }));
    },
    onShowVersions() {
      this.showVersionsModal = true;
    }
  }
};
</script>

<style scoped>
img {
  max-height: 400px;
  max-width: 100%;
}

:deep(.v-chip) {
  opacity: 0.5;

  &.flow-type-CUSTOMIZING {
    background-color: #15b79e;
  }
  &.flow-type-CSV_IMPORTER {
    background-color: #ef6820;
  }
  &.flow-type-PROCESS_ENGINE {
    background-color: #875bf7;
  }
}
</style>
